// ============================================================================
// INTERVIEWER MODULE TYPES
// Unified facet extraction with source type context
// ============================================================================

// ============================================================================
// CORE TYPES
// ============================================================================

// Source type provides context hints but doesn't restrict facet extraction
export type SourceType =
  | 'generic'           // Default - any text input
  | 'user_interview'    // Customer/user conversation
  | 'buyer_interview'   // Buyer/decision-maker conversation
  | 'stakeholder_interview' // Internal stakeholder conversation
  | 'customer_interview' // Customer interview (general)
  | 'discovery_meeting' // Discovery meeting
  | 'validation_meeting' // Validation meeting
  | 'document'          // Uploaded document (PDF, notes, etc.)
  | 'issue'             // GitHub/Jira issue, bug report
  | 'url'               // Web content, article, competitor page
  | 'meeting_notes'     // Meeting transcript or notes
  | 'support_ticket'    // Support conversation

// Legacy alias for backward compatibility
export type InterviewMode = 'discovery' | 'decisions' | 'alignment'

// ============================================================================
// INTERVIEW TYPE CONFIGURATION (Generic Interviewer)
// ============================================================================

export type InterviewTypeId =
  | 'generic'
  | 'user_interview'
  | 'buyer_interview'
  | 'customer_interview'
  | 'stakeholder_interview'
  | 'discovery_meeting'
  | 'validation_meeting'

export interface InterviewTypeConfig {
  id: InterviewTypeId
  label: string
  description: string
  icon: string  // Lucide icon name
  color: string
  linksToPersonEntity: boolean  // Whether this type can link to a Person
  isDefault?: boolean           // Is this the default selection?
  placeholder: string
  tips: string[]
}

export const BUILT_IN_INTERVIEW_TYPES: InterviewTypeConfig[] = [
  {
    id: 'generic',
    label: 'Generic Interview',
    description: 'Analyze any conversation or text content',
    icon: 'FileText',
    color: '#6c757d',
    linksToPersonEntity: false,
    isDefault: true,
    placeholder: 'Paste any interview transcript, conversation, or text content...',
    tips: [
      'Works with any text content',
      'AI will detect all facet types automatically',
      'Great for unstructured content',
    ],
  },
  {
    id: 'user_interview',
    label: 'User Interview',
    description: 'Customer or end-user conversation',
    icon: 'User',
    color: '#4a90a4',
    linksToPersonEntity: true,
    placeholder: 'Paste the user interview transcript...',
    tips: [
      'Link to a Person entity for better tracking',
      'Focus on problems, behaviors, and workarounds',
      'Include exact quotes when possible',
    ],
  },
  {
    id: 'buyer_interview',
    label: 'Buyer Interview',
    description: 'Decision-maker or budget holder conversation',
    icon: 'Briefcase',
    color: '#d4a020',
    linksToPersonEntity: true,
    placeholder: 'Paste the buyer interview transcript...',
    tips: [
      'Link to a Person entity for follow-up',
      'Capture decision criteria and constraints',
      'Note budget and timeline mentions',
    ],
  },
  {
    id: 'customer_interview',
    label: 'Customer Interview',
    description: 'General customer conversation',
    icon: 'UserCircle',
    color: '#5c8a6b',
    linksToPersonEntity: false,
    placeholder: 'Paste the customer interview transcript...',
    tips: [
      'Capture satisfaction and pain points',
      'Note feature requests and feedback',
      'Include context about customer segment',
    ],
  },
  {
    id: 'stakeholder_interview',
    label: 'Stakeholder Interview',
    description: 'Internal stakeholder conversation',
    icon: 'Users',
    color: '#8b5cf6',
    linksToPersonEntity: true,
    placeholder: 'Paste the stakeholder interview transcript...',
    tips: [
      'Link to the stakeholder Person entity',
      'Capture alignment and concerns',
      'Note dependencies and constraints',
    ],
  },
  {
    id: 'discovery_meeting',
    label: 'Discovery Meeting',
    description: 'Initial exploration or problem discovery',
    icon: 'Compass',
    color: '#dc3545',
    linksToPersonEntity: false,
    placeholder: 'Paste the discovery meeting notes...',
    tips: [
      'Focus on problem validation',
      'Capture "aha" moments',
      'Note assumptions to test',
    ],
  },
  {
    id: 'validation_meeting',
    label: 'Validation Meeting',
    description: 'Solution or hypothesis validation',
    icon: 'CheckCircle',
    color: '#22c55e',
    linksToPersonEntity: false,
    placeholder: 'Paste the validation meeting notes...',
    tips: [
      'Capture what was validated or invalidated',
      'Note pivots or adjustments needed',
      'Include feedback on prototypes/mockups',
    ],
  },
]

// Helper to get interview type config by ID
export function getInterviewTypeConfig(id: InterviewTypeId): InterviewTypeConfig {
  return BUILT_IN_INTERVIEW_TYPES.find(t => t.id === id) ?? BUILT_IN_INTERVIEW_TYPES[0]
}

// Helper to get default interview type
export function getDefaultInterviewType(): InterviewTypeConfig {
  return BUILT_IN_INTERVIEW_TYPES.find(t => t.isDefault) ?? BUILT_IN_INTERVIEW_TYPES[0]
}

// Unified facet types - 52 types matching full ontology coverage
export type FacetType =
  // Market Problems & Pains
  | 'problem'
  | 'customer_pain'
  | 'customer_gain'
  | 'customer_job'
  | 'workaround'
  | 'current_behavior'
  // Market Segments & Stakeholders
  | 'persona'
  | 'customer_segment'
  | 'stakeholder'
  | 'person'
  // Competition & Market
  | 'competitor'
  | 'alternative'
  | 'trend'
  | 'industry'
  | 'market_signal'
  // Business Strategy
  | 'strategic_goal'
  | 'decision'
  | 'rationale'
  | 'opportunity'
  | 'value_proposition'
  | 'strategy'
  | 'business_context'
  | 'gtm_strategy'
  | 'positioning'
  // Business Model
  | 'channel'
  | 'customer_relationship'
  | 'revenue_stream'
  | 'key_resource'
  | 'key_activity'
  | 'key_partnership'
  | 'cost_structure'
  // Risks & Weaknesses
  | 'risk'
  | 'threat'
  | 'weakness'
  | 'strength'
  // Solution & Features
  | 'feature_request'
  | 'requirement'
  | 'user_story'
  | 'product'
  | 'theme'
  | 'epic'
  | 'pattern'
  // Constraints & Dependencies
  | 'constraint'
  | 'dependency'
  // Execution
  | 'action_item'
  | 'milestone'
  | 'success_metric'
  // Insights Only (no entity)
  | 'quote'
  | 'context'
  | 'observation'
  // Legacy types (for backwards compatibility)
  | 'willingness_to_pay'
  | 'frequency_mention'
  | 'severity_mention'
  | 'misalignment'
  | 'information_gap'
  | 'escalation_risk'
  | 'communication_pattern'
  | 'market_trend'
  | 'competitor_mention'

export type Severity = 'low' | 'medium' | 'high'
export type Frequency = 'rare' | 'occasional' | 'frequent' | 'constant'
export type DecisionType = 'strategic' | 'tactical' | 'technical' | 'process' | 'tradeoff'
export type MisalignmentPattern = 'terminology' | 'priority' | 'timeline' | 'scope' | 'ownership' | 'constraints'

// ============================================================================
// INPUT SOURCE (replaces Interview for unified approach)
// ============================================================================

export interface InputSource {
  id: string
  sourceType: SourceType
  interviewTypeId?: InterviewTypeId  // Generic interviewer type
  title: string

  // Source context
  interviewee?: string      // For interview types
  personaId?: string
  personaName?: string
  linkedPersonId?: string   // Link to Person entity (for user/buyer/stakeholder interviews)
  linkedPersonName?: string // Cached person name for display
  context: string           // User-provided context

  // Content
  rawContent: string
  sourceUrl?: string        // For url/issue types
  sourceRef?: string        // External reference (issue ID, document path)

  // Processing state
  createdAt: string
  analyzedAt?: string
  status: 'draft' | 'analyzing' | 'analyzed' | 'error' | 'mapping'  // Added 'mapping' for workflow
  errorMessage?: string

  // Extracted data
  facets: Facet[]
  scores: SourceScores
  qualityNotes: QualityNote[]

  // Mapping workflow state
  mappingComplete?: boolean
  mappingDecisions?: FacetMappingDecision[]

  // Organization
  tags?: string[]
  projectId?: string        // Link to project/initiative

  // Traceability
  linkedOntologyItems?: OntologyItemRef[]
}

// Legacy alias for backward compatibility
export interface Interview extends InputSource {
  mode: InterviewMode  // Derived from sourceType
}

export interface OntologyItemRef {
  uri: string
  type: string
  label: string
  linkedAt: string
  linkedBy: 'user' | 'system'
}

export interface SourceScores {
  // Quality metrics (applicable to all source types)
  evidenceStrength?: number    // 1-5
  contextRichness?: number     // 1-5

  // Interview-specific
  momTestScore?: number        // 1-5 (for user/buyer interviews)

  // Decision-specific
  rationaleQuality?: number    // 1-5
  contextPreservation?: number // 1-5

  // Alignment-specific
  alignmentLevel?: number      // 1-5
  actionClarity?: number       // 1-5
}

// Legacy alias
export type InterviewScores = SourceScores

// ============================================================================
// FACETS
// ============================================================================

export interface Facet {
  id: string
  type: FacetType
  content: string
  evidence: string
  evidenceLocation?: {
    start: number
    end: number
    lineNumber?: number
  }
  confidence: number // 0-1

  // Discovery-specific
  severity?: Severity
  frequency?: Frequency
  evidenceStrength?: number // 1-5

  // Decision-specific
  decisionType?: DecisionType
  reversibility?: 'one-way-door' | 'two-way-door'

  // Alignment-specific
  misalignmentPattern?: MisalignmentPattern
  escalationRisk?: Severity

  // Linking
  ontologyLinks: OntologyLink[]
  dismissed: boolean
  dismissedReason?: string

  // Suggested ontology mapping (from AI analysis)
  suggestedOntologyType?: string
  suggestedOntologyReason?: string
}

export interface OntologyLink {
  id: string
  facetId: string
  entityType: string
  entityUri: string
  entityLabel: string
  linkType: 'created' | 'linked' | 'suggested'
  createdAt: string
}

// ============================================================================
// QUALITY NOTES
// ============================================================================

export interface QualityNote {
  id: string
  type: 'warning' | 'suggestion' | 'success' | 'error'
  category: QualityNoteCategory
  message: string
  location?: number
  evidence?: string
}

export type QualityNoteCategory =
  | 'leading_question'
  | 'hypothetical'
  | 'compliment_fishing'
  | 'pitching'
  | 'good_followup'
  | 'past_behavior'
  | 'missing_context'
  | 'missing_rationale'
  | 'unclear_decision'
  | 'missing_stakeholder'

// ============================================================================
// PATTERN CLUSTERING
// ============================================================================

export interface PatternCluster {
  id: string
  name: string
  description: string
  facetIds: string[]
  interviewIds: string[]
  mentionCount: number
  avgEvidenceStrength: number
  avgConfidence: number
  ontologyLinks: OntologyLink[]
  emergingPattern: boolean
  suggestedEntityType?: string
  createdAt: string
  updatedAt: string
}

export interface ClusteringConfig {
  minMentions: number
  minEvidenceStrength: number
  similarityThreshold: number
}

// ============================================================================
// ANALYSIS REQUEST/RESPONSE
// ============================================================================

export interface AnalysisRequest {
  sourceId: string
  sourceType: SourceType
  content: string
  context?: string
  personaContext?: string
  sourceUrl?: string  // For url/issue types
  // Project context for optimized backend extraction
  projectId?: string
  accountId?: string
}

// Legacy alias
export interface LegacyAnalysisRequest {
  interviewId: string
  mode: InterviewMode
  content: string
  context?: string
  personaContext?: string
}

// Extracted entities and relationships for knowledge graph
export interface ExtractedEntity {
  id: string
  type: string
  name: string
  description: string
  evidence: string
  confidence: number
}

export interface ExtractedRelationship {
  from: string
  to: string
  type: string
  evidence: string
  confidence: number
}

export interface AnalysisResponse {
  facets: Facet[]
  entities?: ExtractedEntity[]
  relationships?: ExtractedRelationship[]
  scores: InterviewScores
  qualityNotes: QualityNote[]
  suggestedLinks?: SuggestedOntologyLink[]
}

export interface SuggestedOntologyLink {
  facetId: string
  entityType: string
  entityUri?: string
  entityLabel: string
  reason: string
  confidence: number
}

// ============================================================================
// UI STATE
// ============================================================================

export interface InterviewerState {
  sources: InputSource[]
  currentSource: InputSource | null
  clusters: PatternCluster[]
  isAnalyzing: boolean
  selectedFacetId: string | null
  filterSourceType: SourceType | 'all'
  filterCategory: FacetCategory | 'all'
  searchQuery: string

  // Legacy aliases
  interviews: Interview[]
  currentInterview: Interview | null
  filterMode: InterviewMode | 'all'
}

export interface FacetFilterOptions {
  types?: FacetType[]
  minConfidence?: number
  minEvidenceStrength?: number
  showDismissed?: boolean
  linkedOnly?: boolean
  unlinkedOnly?: boolean
}

// ============================================================================
// EXPORT/IMPORT
// ============================================================================

export interface InterviewExport {
  version: string
  exportedAt: string
  interviews: Interview[]
  clusters: PatternCluster[]
}

// ============================================================================
// FACET TYPE METADATA
// ============================================================================

// Facet category for grouping in UI (domain-based)
export type FacetCategory = 'market' | 'business' | 'solution' | 'insights'

export const FACET_TYPE_CONFIG: Record<FacetType, {
  label: string
  color: string
  icon: string
  description: string
  category: FacetCategory
  ontologyTypes: string[]  // Suggested ontology entity types
}> = {
  // === MARKET: Customer understanding, problems, competition ===
  problem: {
    label: 'Problem',
    color: '#5c8a6b',
    icon: 'AlertCircle',
    description: 'A pain point or challenge',
    category: 'market',
    ontologyTypes: ['Problem', 'CustomerPain'],
  },
  current_behavior: {
    label: 'Current Behavior',
    color: '#5c8a6b',
    icon: 'Activity',
    description: 'What they are doing today',
    category: 'market',
    ontologyTypes: ['Workaround', 'KeyActivity'],
  },
  workaround: {
    label: 'Workaround',
    color: '#5c8a6b',
    icon: 'RefreshCw',
    description: 'A solution they built or process they follow',
    category: 'market',
    ontologyTypes: ['Workaround'],
  },
  willingness_to_pay: {
    label: 'Willingness to Pay',
    color: '#5c8a6b',
    icon: 'DollarSign',
    description: 'Evidence they have or would spend money/time',
    category: 'market',
    ontologyTypes: ['CustomerPain', 'ValueProposition'],
  },
  frequency_mention: {
    label: 'Frequency',
    color: '#5c8a6b',
    icon: 'Clock',
    description: 'How often the problem occurs',
    category: 'market',
    ontologyTypes: ['CustomerPain'],
  },
  severity_mention: {
    label: 'Severity',
    color: '#5c8a6b',
    icon: 'AlertTriangle',
    description: 'How impactful the problem is',
    category: 'market',
    ontologyTypes: ['CustomerPain'],
  },
  stakeholder: {
    label: 'Stakeholder',
    color: '#5c8a6b',
    icon: 'Users',
    description: 'A person or team involved',
    category: 'market',
    ontologyTypes: ['Person', 'Stakeholder'],
  },
  alternative: {
    label: 'Alternative',
    color: '#5c8a6b',
    icon: 'GitBranch',
    description: 'An alternative solution or competitor',
    category: 'market',
    ontologyTypes: ['Alternative', 'Competitor'],
  },
  market_trend: {
    label: 'Market Trend',
    color: '#5c8a6b',
    icon: 'TrendingUp',
    description: 'Industry or market trend mentioned',
    category: 'market',
    ontologyTypes: ['Trend'],
  },
  competitor_mention: {
    label: 'Competitor',
    color: '#5c8a6b',
    icon: 'Target',
    description: 'Competitor or alternative solution mentioned',
    category: 'market',
    ontologyTypes: ['Competitor'],
  },

  // === BUSINESS: Strategy, decisions, business model ===
  decision: {
    label: 'Decision',
    color: '#4a90a4',
    icon: 'CheckSquare',
    description: 'A decision that was made',
    category: 'business',
    ontologyTypes: ['Decision'],
  },
  rationale: {
    label: 'Rationale',
    color: '#4a90a4',
    icon: 'Lightbulb',
    description: 'The reasoning behind a decision',
    category: 'business',
    ontologyTypes: ['Decision'],
  },
  constraint: {
    label: 'Constraint',
    color: '#4a90a4',
    icon: 'Lock',
    description: 'A limitation that affected the decision',
    category: 'business',
    ontologyTypes: ['Constraint', 'Risk'],
  },
  dependency: {
    label: 'Dependency',
    color: '#4a90a4',
    icon: 'Link',
    description: 'Something this decision blocks or enables',
    category: 'business',
    ontologyTypes: ['Dependency'],
  },
  business_context: {
    label: 'Business Context',
    color: '#4a90a4',
    icon: 'Briefcase',
    description: 'Business goals, metrics, or strategic context',
    category: 'business',
    ontologyTypes: ['BusinessContext', 'StrategicGoal'],
  },
  opportunity: {
    label: 'Opportunity',
    color: '#4a90a4',
    icon: 'Sparkles',
    description: 'A potential opportunity identified',
    category: 'business',
    ontologyTypes: ['Opportunity', 'ValueProposition'],
  },
  risk: {
    label: 'Risk',
    color: '#4a90a4',
    icon: 'AlertTriangle',
    description: 'A business or technical risk',
    category: 'business',
    ontologyTypes: ['Risk', 'Threat'],
  },
  success_metric: {
    label: 'Success Metric',
    color: '#4a90a4',
    icon: 'BarChart',
    description: 'KPI or success criteria mentioned',
    category: 'business',
    ontologyTypes: ['SuccessMetric', 'StrategicGoal'],
  },

  // === SOLUTION: Product, features, delivery ===
  action_item: {
    label: 'Action Item',
    color: '#d4a020',
    icon: 'CheckCircle',
    description: 'A task or next step identified',
    category: 'solution',
    ontologyTypes: ['ActionItem', 'Milestone'],
  },
  milestone: {
    label: 'Milestone',
    color: '#d4a020',
    icon: 'Flag',
    description: 'A project milestone or deadline',
    category: 'solution',
    ontologyTypes: ['Milestone'],
  },
  feature_request: {
    label: 'Feature Request',
    color: '#d4a020',
    icon: 'Sparkles',
    description: 'A requested product feature',
    category: 'solution',
    ontologyTypes: ['Feature'],
  },
  requirement: {
    label: 'Requirement',
    color: '#d4a020',
    icon: 'FileCheck',
    description: 'A must-have requirement',
    category: 'solution',
    ontologyTypes: ['Requirement'],
  },
  user_story: {
    label: 'User Story',
    color: '#d4a020',
    icon: 'BookOpen',
    description: 'A user story or use case',
    category: 'solution',
    ontologyTypes: ['UserStory'],
  },
  product: {
    label: 'Product',
    color: '#d4a020',
    icon: 'Package',
    description: 'A product or service mention',
    category: 'solution',
    ontologyTypes: ['Product'],
  },
  theme: {
    label: 'Theme',
    color: '#d4a020',
    icon: 'Layers',
    description: 'A strategic theme or initiative',
    category: 'solution',
    ontologyTypes: ['Theme'],
  },
  epic: {
    label: 'Epic',
    color: '#d4a020',
    icon: 'FolderOpen',
    description: 'A large body of work',
    category: 'solution',
    ontologyTypes: ['Epic'],
  },
  pattern: {
    label: 'Pattern',
    color: '#d4a020',
    icon: 'Grid',
    description: 'A design or architecture pattern',
    category: 'solution',
    ontologyTypes: ['Pattern'],
  },

  // === NEW MARKET TYPES ===
  customer_pain: {
    label: 'Customer Pain',
    color: '#5c8a6b',
    icon: 'Frown',
    description: 'Negative emotions, costs, or risks customers avoid',
    category: 'market',
    ontologyTypes: ['CustomerPain'],
  },
  customer_gain: {
    label: 'Customer Gain',
    color: '#5c8a6b',
    icon: 'Smile',
    description: 'Outcomes and benefits customers want',
    category: 'market',
    ontologyTypes: ['CustomerGain'],
  },
  customer_job: {
    label: 'Customer Job',
    color: '#5c8a6b',
    icon: 'Clipboard',
    description: 'Tasks customers are trying to accomplish',
    category: 'market',
    ontologyTypes: ['CustomerJob'],
  },
  persona: {
    label: 'Persona',
    color: '#5c8a6b',
    icon: 'User',
    description: 'A user archetype or customer profile',
    category: 'market',
    ontologyTypes: ['Persona'],
  },
  customer_segment: {
    label: 'Customer Segment',
    color: '#5c8a6b',
    icon: 'Users',
    description: 'A group of customers with shared characteristics',
    category: 'market',
    ontologyTypes: ['CustomerSegment'],
  },
  person: {
    label: 'Person',
    color: '#5c8a6b',
    icon: 'UserCircle',
    description: 'A specific individual mentioned',
    category: 'market',
    ontologyTypes: ['Person'],
  },
  competitor: {
    label: 'Competitor',
    color: '#5c8a6b',
    icon: 'Swords',
    description: 'A competing product, service, or company',
    category: 'market',
    ontologyTypes: ['Competitor'],
  },
  trend: {
    label: 'Trend',
    color: '#5c8a6b',
    icon: 'TrendingUp',
    description: 'An industry or market trend',
    category: 'market',
    ontologyTypes: ['Trend'],
  },
  industry: {
    label: 'Industry',
    color: '#5c8a6b',
    icon: 'Building',
    description: 'Industry or market sector reference',
    category: 'market',
    ontologyTypes: ['Industry'],
  },
  market_signal: {
    label: 'Market Signal',
    color: '#5c8a6b',
    icon: 'Radio',
    description: 'Weak or strong signal from the market',
    category: 'market',
    ontologyTypes: ['MarketSignal'],
  },

  // === NEW BUSINESS TYPES ===
  strategic_goal: {
    label: 'Strategic Goal',
    color: '#4a90a4',
    icon: 'Target',
    description: 'High-level business objective',
    category: 'business',
    ontologyTypes: ['StrategicGoal'],
  },
  value_proposition: {
    label: 'Value Proposition',
    color: '#4a90a4',
    icon: 'Diamond',
    description: 'The value offered to customers',
    category: 'business',
    ontologyTypes: ['ValueProposition'],
  },
  strategy: {
    label: 'Strategy',
    color: '#4a90a4',
    icon: 'Compass',
    description: 'Strategic direction or approach',
    category: 'business',
    ontologyTypes: ['Strategy'],
  },
  gtm_strategy: {
    label: 'GTM Strategy',
    color: '#4a90a4',
    icon: 'Rocket',
    description: 'Go-to-market approach',
    category: 'business',
    ontologyTypes: ['GoToMarketStrategy'],
  },
  positioning: {
    label: 'Positioning',
    color: '#4a90a4',
    icon: 'MapPin',
    description: 'Market positioning or differentiation',
    category: 'business',
    ontologyTypes: ['Positioning'],
  },
  channel: {
    label: 'Channel',
    color: '#4a90a4',
    icon: 'Share2',
    description: 'Touchpoint for reaching customers',
    category: 'business',
    ontologyTypes: ['Channel'],
  },
  customer_relationship: {
    label: 'Customer Relationship',
    color: '#4a90a4',
    icon: 'Heart',
    description: 'Type of relationship with customers',
    category: 'business',
    ontologyTypes: ['CustomerRelationship'],
  },
  revenue_stream: {
    label: 'Revenue Stream',
    color: '#4a90a4',
    icon: 'DollarSign',
    description: 'How the business earns money',
    category: 'business',
    ontologyTypes: ['RevenueStream'],
  },
  key_resource: {
    label: 'Key Resource',
    color: '#4a90a4',
    icon: 'Box',
    description: 'Critical assets needed',
    category: 'business',
    ontologyTypes: ['KeyResource'],
  },
  key_activity: {
    label: 'Key Activity',
    color: '#4a90a4',
    icon: 'Activity',
    description: 'Critical activities',
    category: 'business',
    ontologyTypes: ['KeyActivity'],
  },
  key_partnership: {
    label: 'Key Partnership',
    color: '#4a90a4',
    icon: 'Handshake',
    description: 'Strategic partners and suppliers',
    category: 'business',
    ontologyTypes: ['KeyPartnership'],
  },
  cost_structure: {
    label: 'Cost Structure',
    color: '#4a90a4',
    icon: 'Receipt',
    description: 'Costs incurred',
    category: 'business',
    ontologyTypes: ['CostStructure'],
  },
  threat: {
    label: 'Threat',
    color: '#4a90a4',
    icon: 'AlertOctagon',
    description: 'An external threat',
    category: 'business',
    ontologyTypes: ['Threat'],
  },
  weakness: {
    label: 'Weakness',
    color: '#4a90a4',
    icon: 'TrendingDown',
    description: 'An internal weakness or gap',
    category: 'business',
    ontologyTypes: ['Weakness'],
  },
  strength: {
    label: 'Strength',
    color: '#4a90a4',
    icon: 'Shield',
    description: 'An internal strength or advantage',
    category: 'business',
    ontologyTypes: ['Strength'],
  },

  // === INSIGHTS: Context, observations (no entity created) ===
  misalignment: {
    label: 'Misalignment',
    color: '#6c757d',
    icon: 'AlertCircle',
    description: 'A point where understanding diverges',
    category: 'insights',
    ontologyTypes: [],
  },
  information_gap: {
    label: 'Information Gap',
    color: '#6c757d',
    icon: 'HelpCircle',
    description: 'Context that was not shared effectively',
    category: 'insights',
    ontologyTypes: [],
  },
  escalation_risk: {
    label: 'Escalation Risk',
    color: '#6c757d',
    icon: 'TrendingUp',
    description: 'An issue likely to grow if unaddressed',
    category: 'insights',
    ontologyTypes: [],
  },
  communication_pattern: {
    label: 'Communication Pattern',
    color: '#6c757d',
    icon: 'MessageCircle',
    description: 'A recurring communication behavior',
    category: 'insights',
    ontologyTypes: [],
  },
  quote: {
    label: 'Notable Quote',
    color: '#6c757d',
    icon: 'Quote',
    description: 'A significant quote worth preserving',
    category: 'insights',
    ontologyTypes: [],
  },
  context: {
    label: 'Context',
    color: '#6c757d',
    icon: 'Info',
    description: 'Background context for understanding',
    category: 'insights',
    ontologyTypes: [],
  },
  observation: {
    label: 'Observation',
    color: '#6c757d',
    icon: 'Eye',
    description: 'A general observation or note',
    category: 'insights',
    ontologyTypes: [],
  },
}

// Source type metadata for UI
export const SOURCE_TYPE_CONFIG: Record<SourceType, {
  label: string
  icon: string
  description: string
  placeholder: string
}> = {
  generic: {
    label: 'Generic',
    icon: 'FileText',
    description: 'Any text content',
    placeholder: 'Paste any text content to analyze...',
  },
  user_interview: {
    label: 'User Interview',
    icon: 'User',
    description: 'Customer or user conversation',
    placeholder: 'Paste interview transcript or notes...',
  },
  buyer_interview: {
    label: 'Buyer Interview',
    icon: 'Briefcase',
    description: 'Buyer or decision-maker conversation',
    placeholder: 'Paste interview transcript or notes...',
  },
  stakeholder_interview: {
    label: 'Stakeholder Interview',
    icon: 'Users',
    description: 'Internal stakeholder conversation',
    placeholder: 'Paste interview transcript or notes...',
  },
  customer_interview: {
    label: 'Customer Interview',
    icon: 'UserCircle',
    description: 'General customer conversation',
    placeholder: 'Paste interview transcript or notes...',
  },
  discovery_meeting: {
    label: 'Discovery Meeting',
    icon: 'Compass',
    description: 'Initial exploration or problem discovery',
    placeholder: 'Paste meeting notes or transcript...',
  },
  validation_meeting: {
    label: 'Validation Meeting',
    icon: 'CheckCircle',
    description: 'Solution or hypothesis validation',
    placeholder: 'Paste meeting notes or transcript...',
  },
  document: {
    label: 'Document',
    icon: 'FileText',
    description: 'Uploaded document or notes',
    placeholder: 'Paste document content...',
  },
  issue: {
    label: 'Issue/Ticket',
    icon: 'Bug',
    description: 'GitHub/Jira issue or bug report',
    placeholder: 'Paste issue content or provide URL...',
  },
  url: {
    label: 'Web Content',
    icon: 'Globe',
    description: 'Article, competitor page, or web content',
    placeholder: 'Enter URL or paste content...',
  },
  meeting_notes: {
    label: 'Meeting Notes',
    icon: 'Calendar',
    description: 'Meeting transcript or notes',
    placeholder: 'Paste meeting notes or transcript...',
  },
  support_ticket: {
    label: 'Support Ticket',
    icon: 'MessageSquare',
    description: 'Support conversation or ticket',
    placeholder: 'Paste support conversation...',
  },
}

// ============================================================================
// SCORE CRITERIA
// ============================================================================

export const MOM_TEST_SCORE_CRITERIA: Record<number, string> = {
  5: 'Only asked about past behavior, no hypotheticals, no leading questions',
  4: 'Mostly past behavior, minor hypothetical slip',
  3: 'Mix of past behavior and future intentions',
  2: 'Mostly hypotheticals, some past behavior',
  1: 'Leading questions, seeking validation, no past behavior',
}

export const EVIDENCE_STRENGTH_CRITERIA: Record<number, string> = {
  5: 'They paid money/significant time to solve this already',
  4: 'They actively tried workarounds, spent real effort',
  3: 'They\'ve complained to others, clear frustration',
  2: 'They acknowledge the problem when asked',
  1: 'They agree it "sounds like a problem"',
}

export const RATIONALE_QUALITY_CRITERIA: Record<number, string> = {
  5: 'Clear problem, alternatives listed, constraints explained, future team would fully understand',
  4: 'Good context, minor gaps in alternatives or constraints',
  3: 'Decision clear, rationale partial',
  2: 'Decision recorded but "why" is vague',
  1: 'Just the decision, no context',
}

// ============================================================================
// ONTOLOGY MAPPING
// ============================================================================

// Helper to get ontology types for a facet type
export function getOntologyTypesForFacet(facetType: FacetType): string[] {
  return FACET_TYPE_CONFIG[facetType]?.ontologyTypes ?? []
}

// Helper to convert SourceType to legacy InterviewMode
export function sourceTypeToMode(sourceType: SourceType): InterviewMode {
  switch (sourceType) {
    case 'user_interview':
    case 'buyer_interview':
    case 'customer_interview':
    case 'support_ticket':
    case 'discovery_meeting':
      return 'discovery'
    case 'stakeholder_interview':
    case 'meeting_notes':
      return 'alignment'
    case 'validation_meeting':
    default:
      return 'decisions' // Default for generic/document/issue/url
  }
}

// ============================================================================
// FACET CATEGORY CONFIGURATION (for grouped display in UI)
// ============================================================================

export interface FacetCategoryConfig {
  id: FacetCategory
  label: string
  description: string
  icon: string
  color: string
  sortOrder: number
}

export const FACET_CATEGORY_CONFIG: Record<FacetCategory, FacetCategoryConfig> = {
  market: {
    id: 'market',
    label: 'Market',
    description: 'Understanding customers, problems, and competition',
    icon: 'Target',
    color: '#5c8a6b',
    sortOrder: 1,
  },
  business: {
    id: 'business',
    label: 'Business',
    description: 'Strategy, decisions, and business model',
    icon: 'Briefcase',
    color: '#4a90a4',
    sortOrder: 2,
  },
  solution: {
    id: 'solution',
    label: 'Solution',
    description: 'Product, features, and delivery',
    icon: 'Lightbulb',
    color: '#d4a020',
    sortOrder: 3,
  },
  insights: {
    id: 'insights',
    label: 'Insights',
    description: 'Quotes, observations, and context (no entity)',
    icon: 'MessageCircle',
    color: '#6c757d',
    sortOrder: 4,
  },
}

// Helper to get facets grouped by category
export function groupFacetsByCategory(facets: Facet[]): Record<FacetCategory, Facet[]> {
  const grouped: Record<FacetCategory, Facet[]> = {
    market: [],
    business: [],
    solution: [],
    insights: [],
  }

  for (const facet of facets) {
    const category = FACET_TYPE_CONFIG[facet.type]?.category ?? 'market'
    grouped[category].push(facet)
  }

  return grouped
}

// ============================================================================
// MAPPING WORKFLOW TYPES
// ============================================================================

export type MappingAction =
  | 'accept_suggestion'   // Accept AI's suggested ontology mapping
  | 'link_existing'       // Link to an existing ontology entity
  | 'create_new'          // Create a new ontology entity
  | 'skip'                // Skip mapping this facet

export interface FacetMappingDecision {
  facetId: string
  action: MappingAction

  // For accept_suggestion or link_existing
  linkedEntityUri?: string
  linkedEntityType?: string
  linkedEntityLabel?: string

  // For create_new
  newEntityType?: string
  newEntityLabel?: string
  newEntityDescription?: string

  // Metadata
  decidedAt: string
  aiConfidence?: number    // Original AI confidence for the suggestion
  aiSuggestedType?: string // What AI suggested before user decision
}

export interface FacetWithMapping extends Facet {
  mappingDecision?: FacetMappingDecision
  suggestedMapping?: {
    entityType: string
    entityLabel: string
    confidence: number
    reason: string
    existingMatch?: {
      uri: string
      label: string
      similarity: number
    }
  }
}

// Helper to check if all facets have mapping decisions
export function isAllFacetsMapped(facets: Facet[], decisions: FacetMappingDecision[]): boolean {
  const activeFacets = facets.filter(f => !f.dismissed)
  const decisionMap = new Map(decisions.map(d => [d.facetId, d]))
  return activeFacets.every(f => decisionMap.has(f.id))
}

// Helper to get summary of mapping decisions
export interface MappingSummary {
  total: number
  accepted: number
  linked: number
  created: number
  skipped: number
}

export function getMappingSummary(decisions: FacetMappingDecision[]): MappingSummary {
  return {
    total: decisions.length,
    accepted: decisions.filter(d => d.action === 'accept_suggestion').length,
    linked: decisions.filter(d => d.action === 'link_existing').length,
    created: decisions.filter(d => d.action === 'create_new').length,
    skipped: decisions.filter(d => d.action === 'skip').length,
  }
}
